import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BadgeCheck, Pencil, Plus, Shield, Trash2 } from 'lucide-react';
import { Button } from '@roger/ui-components';
import {
  deletePatientInsurance,
  listPatientInsurances,
} from '../../services/api';
import type { EligibilityCheckResponse } from '../../services/api';
import type { PatientInsuranceResponse } from '../../services/api/types.gen';
import {
  formatDateInAgencyZone,
  formatDateToMMDDYYYY,
  formatTimeInAgencyZone,
} from '../../utils/dateUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';
import { PermissionSlug } from '../../constants/featureFlags';
import EligibilityDetailsModal from '../EligibilityDetailsModal';
import { EligibilityCheckSummary } from '../eligibility/EligibilityCheckSummary';
import { RunEligibilityCheckButton } from '../eligibility/RunEligibilityCheckButton';
import { OtherAgencyPeriodsOfCare } from '../eligibility/OtherAgencyPeriodsOfCare';
import { latestCmsAnswer } from '../eligibility/eligibilityStatus';
import { usePatientEligibilityChecks } from '../../hooks/usePatientEligibilityChecks';

interface InsuranceTabProps {
  patientId: string;
  onAddInsurance?: () => void;
  onEditInsurance?: (insuranceId: string) => void;
  /** Bumped by the parent when the add/edit dialog saves, to force a reload. */
  insuranceRefreshTrigger?: number;
  /**
   * Called after this tab changes the list itself (delete), so the parent can
   * refetch the copy it feeds to PatientHeader and EpisodeInsuranceSelector.
   */
  onInsurancesChanged?: () => void;
  /** Called after an eligibility check lands so the Insurance tab badge refreshes. */
  onEligibilityChanged?: () => void;
}

/**
 * The patient's insurance coverage.
 *
 * Moved out of Patient Info so coverage lives next to Authorizations: insurance
 * answers "who pays and is the patient covered", authorizations answer "what was
 * approved". Patient Info keeps demographics.
 */
export const InsuranceTab: React.FC<InsuranceTabProps> = ({
  patientId,
  onAddInsurance,
  onEditInsurance,
  insuranceRefreshTrigger,
  onInsurancesChanged,
  onEligibilityChanged,
}) => {
  const { error: showError } = useToast();
  const [insurances, setInsurances] = useState<PatientInsuranceResponse[]>([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [detailCheck, setDetailCheck] =
    useState<EligibilityCheckResponse | null>(null);

  const canRunEligibility = useStore(state =>
    state.hasPermission(PermissionSlug.ELIGIBILITY_CHECK)
  );
  const {
    checks,
    latestFor,
    blockedReasonFor,
    reload: reloadChecks,
    loading: checksLoading,
    failed: checksFailed,
    resolving: checksResolving,
  } = usePatientEligibilityChecks(patientId);

  // Bumped on every load so a response that resolves after a newer load (or
  // after a patient switch) cannot write its rows into the current chart.
  const loadSeqRef = useRef(0);

  const loadInsurances = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setInsurancesLoading(true);
    try {
      // The API client is configured throwOnError:false, so an HTTP failure
      // resolves to `{ error }` rather than throwing — inspect it explicitly.
      const res = await listPatientInsurances({
        path: { patient_id: patientId },
      });
      if (seq !== loadSeqRef.current) return;
      if (res.error) {
        console.error('Failed to load patient insurances:', res.error);
        showError('Unable to load insurance information. Please try again.');
        return;
      }
      if (res.data) setInsurances(res.data);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Failed to load patient insurances:', err);
      showError('Unable to load insurance information. Please try again.');
    } finally {
      if (seq === loadSeqRef.current) setInsurancesLoading(false);
    }
  }, [patientId, showError]);

  useEffect(() => {
    void loadInsurances();
    return () => {
      // Discard any in-flight response for the patient we are leaving.
      loadSeqRef.current++;
    };
  }, [loadInsurances]);

  useEffect(() => {
    if (insuranceRefreshTrigger !== undefined && insuranceRefreshTrigger > 0) {
      void loadInsurances();
    }
  }, [insuranceRefreshTrigger, loadInsurances]);

  const handleDeleteInsurance = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      // The API client is configured throwOnError:false, so an HTTP failure
      // resolves to `{ error }` rather than throwing — inspect it explicitly.
      const resp = await deletePatientInsurance({
        path: { patient_id: patientId, patient_insurance_id: id },
      });
      if (resp.error) {
        console.error('Failed to delete insurance:', resp.error);
        showError(
          getBusinessErrorMessage(resp) ??
            'Unable to delete insurance. Please try again.'
        );
        return;
      }
      onInsurancesChanged?.();
      // Deleting a lapsed coverage clears the problem server-side; refresh the
      // Insurance-tab badge without waiting for a new eligibility check.
      onEligibilityChanged?.();
      await loadInsurances();
    } catch (err: unknown) {
      console.error('Failed to delete insurance:', err);
      showError('Unable to delete insurance. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className='space-y-4'>
      {/* GET ?patient_id needs eligibility:check, so without it nothing loads. */}
      {canRunEligibility && !checksResolving && !checksFailed && (
        <OtherAgencyPeriodsOfCare check={latestCmsAnswer(checks)} />
      )}
      {/* Panel, not Card: the tab already renders inside PatientDetail's Card,
          so a Card here nests one inside another. Matches EventsTab and
          AuthorizationsTab. */}
      <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
        <div className='flex flex-col gap-2 border-b border-border/60 bg-muted/20 px-3 py-1.5 sm:flex-row sm:items-center sm:justify-between'>
          <h3 className='flex items-center gap-2 text-sm font-medium text-muted-foreground'>
            <Shield className='h-4 w-4' />
            Insurance Information
          </h3>
          {onAddInsurance && (
            <Button size='sm' variant='outline' onClick={onAddInsurance}>
              <Plus className='h-3.5 w-3.5 mr-1' />
              Add Insurance
            </Button>
          )}
        </div>
        <div className='px-3'>
          {insurancesLoading ? (
            <p className='text-sm text-muted-foreground py-6 text-center'>
              Loading…
            </p>
          ) : insurances.length === 0 ? (
            <p className='text-sm text-muted-foreground py-6 text-center'>
              No insurance policies on file.
            </p>
          ) : (
            <div className='divide-y divide-border/60'>
              {insurances.map(ins => (
                <div
                  key={ins.id}
                  className='py-3 flex items-center justify-between gap-3 group'
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    <div className='min-w-0'>
                      <p className='text-sm font-medium truncate'>
                        {ins.insurance_name}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {[
                          ins.plan_name,
                          ins.policy_number
                            ? `Policy: ${ins.policy_number}`
                            : null,
                          ins.effective_start
                            ? `${formatDateToMMDDYYYY(ins.effective_start)}${ins.effective_end ? ` – ${formatDateToMMDDYYYY(ins.effective_end)}` : ''}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      <div className='mt-1'>
                        <EligibilityCheckSummary
                          check={latestFor(ins)}
                          loading={checksResolving}
                          failed={checksFailed}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-1'>
                    {canRunEligibility && (
                      <RunEligibilityCheckButton
                        patientId={patientId}
                        patientInsuranceId={ins.id}
                        blockedReason={blockedReasonFor(ins)}
                        label='Check eligibility'
                        onChecked={() => {
                          void reloadChecks();
                          onEligibilityChanged?.();
                        }}
                      />
                    )}
                    {onEditInsurance && (
                      <button
                        type='button'
                        onClick={() => onEditInsurance(ins.id)}
                        className='shrink-0 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
                        aria-label='Edit insurance'
                      >
                        <Pencil className='h-3.5 w-3.5' />
                      </button>
                    )}
                    <button
                      type='button'
                      disabled={insurances.length <= 1 || deletingId !== null}
                      onClick={e => void handleDeleteInsurance(ins.id, e)}
                      className='shrink-0 p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                      aria-label='Delete insurance'
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Gated on the same slug as running a check: the history and its 271
          detail carry the payer's benefit and deductible response, so a role
          that may not run a check may not read one either. */}
      {canRunEligibility && (
        <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
          <h3 className='flex items-center gap-2 border-b border-border/60 bg-muted/20 px-3 py-1.5 text-sm font-medium text-muted-foreground'>
            <BadgeCheck className='h-4 w-4' />
            Eligibility Checks
          </h3>
          <div className='px-3'>
            {checksFailed ? (
              <p className='text-sm text-muted-foreground py-6 text-center'>
                Eligibility history could not be loaded.
              </p>
            ) : checksLoading ? (
              <p className='text-sm text-muted-foreground py-6 text-center'>
                Loading…
              </p>
            ) : checks.length === 0 ? (
              <p className='text-sm text-muted-foreground py-6 text-center'>
                No eligibility checks have been run for this patient.
              </p>
            ) : (
              <div className='divide-y divide-border/60'>
                {checks.map(check => (
                  <button
                    key={check.id}
                    type='button'
                    onClick={() => setDetailCheck(check)}
                    className='w-full py-3 flex items-center justify-between gap-3 text-left hover:bg-muted/50 transition-colors'
                  >
                    <div className='min-w-0'>
                      <p className='text-sm font-medium truncate'>
                        {check.payor_name ?? 'Eligibility check'}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {formatDateInAgencyZone(
                          check.response_date ?? check.request_date
                        )}{' '}
                        {formatTimeInAgencyZone(
                          check.response_date ?? check.request_date
                        )}
                      </p>
                    </div>
                    <EligibilityCheckSummary check={check} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {canRunEligibility && (
        <EligibilityDetailsModal
          isOpen={detailCheck !== null}
          onClose={() => setDetailCheck(null)}
          eligibilityResult={detailCheck}
        />
      )}
    </div>
  );
};
